import { addWeeks, differenceInCalendarDays, endOfDay, nextSunday } from 'date-fns';
import { useContext, useMemo, useState } from 'react';

import defaultTimeZone from '@/constants/defaultTimeZone';
import { useScheduleItems } from '@/hooks/useAppointments';
import PersonContext from '@/pages/Person/PersonContext';
import { Typography } from '@/ui';
import { ShowMoreButton } from '@/ui/ShowMoreButton';
import { formatTimeOfDayInZone, formatWeekdayDate } from '@/utils/dates';
import formatStaffName from '@/utils/formatStaffName';

const DEFAULT_VISIBLE_COUNT = 2;
const STALE_TIME_MS = 30_000;
const ACTIVE_ATTENDANCE_STATES = ['pending', 'pending_attendance', 'confirmed'];

const ScheduleSummary = () => {
  const { person } = useContext(PersonContext);
  const [showAll, setShowAll] = useState(false);

  // Rest of this week plus all of next week, so the window is stable across
  // renders while still covering appointments near the end of a 7-day rolling window.
  const endAt = useMemo(() => endOfDay(addWeeks(nextSunday(new Date()), 1)), []);
  const windowDays = useMemo(() => differenceInCalendarDays(endAt, new Date()), [endAt]);

  const { data } = useScheduleItems(
    {
      personId: person?.id ?? '',
      endAt,
      objectType: 'attendance',
      attendanceStates: ACTIVE_ATTENDANCE_STATES
    },
    { staleTime: STALE_TIME_MS, enabled: Boolean(person?.id) }
  );

  const items = data ?? [];
  const visibleItems = showAll ? items : items.slice(0, DEFAULT_VISIBLE_COUNT);
  const timeZone = person?.time_zone || defaultTimeZone;

  return items.length > 0 ? (
    <div className="mt-2">
      {visibleItems.map((item) => {
        const { start_at } = item;
        const title = item.attendance[0]?.appointment.title;
        const provider = item.provider?.[0];

        return (
          <div key={item.id} className="flex items-baseline gap-2 py-0.5">
            {start_at && (
              <>
                <Typography className="whitespace-nowrap">
                  {formatWeekdayDate(start_at, timeZone)}
                </Typography>
                <Typography muted className="whitespace-nowrap">
                  {formatTimeOfDayInZone(start_at, timeZone)}
                </Typography>
              </>
            )}
            <Typography className="truncate">
              {title}
              {provider && ` · ${formatStaffName({ staff: provider })}`}
            </Typography>
          </div>
        );
      })}

      {items.length > DEFAULT_VISIBLE_COUNT && (
        <ShowMoreButton isExpanded={showAll} onToggle={() => setShowAll(!showAll)} />
      )}
    </div>
  ) : (
    <Typography className="mt-2" muted>
      No upcoming appointments in the next {windowDays} days
    </Typography>
  );
};

export default ScheduleSummary;
